import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Container, Row, Col, Button } from "react-bootstrap";
import "./CaseStudyDetails.css";

// Import components
import CustomNavbar from "../components/CustomNavbar";
import Footer from "../components/Footer";

// Import images
import case1 from "../assets/project-1.jpg";
import case2 from "../assets/project-2.jpg";
import case3 from "../assets/project-3.jpg";
import case4 from "../assets/project-3.jpg";

const caseStudies = [
  {
    id: 1,
    title: "Financial Services Digital Transformation",
    category: "Enabling a Sales Channel Through Agile Digital Transformation",
    image: case1,
    challenge:
      "A leading retail financial services group needed to digitize its in-store insurance sales process. The existing method was manual, prone to errors, and struggled to meet regulatory compliance standards, risking revenue and reputation.",
    solution:
      "We worked with the client's leadership to architect and deliver a bespoke sales enablement platform. Acting as the Project Manager, we led a hybrid-agile project, managing backlogs, prioritizing features, and bridging the gap between business stakeholders and the development teams (UI/UX, .NET back-end, React front-end). We implemented a rigorous workflow to ensure every feature met both business objectives and strict compliance requirements.",
    results: [
      "100% Regulatory Compliance achieved for the new digital sales process.",
      "Streamlined Data Capture reduced administrative overhead by an estimated 30%.",
      "Successful End-to-End Delivery of a critical platform on time and within its allocated budget.",
      "Enabled a new, scalable revenue channel for the client."
    ],
    keyServices: [
      "Digital Transformation",
      "Agile Project Management",
      "Product Ownership",
      "Regulatory Compliance",
      "Stakeholder Management"
    ],
  },
  {
    id: 2,
    title: "Telecommunications Infrastructure Rollout",
    category: "Building Redundant Fiber Networks to Future-Proof Growth for an ISP",
    image: case2,
    challenge:
      " A growing internet service provider needed to design and build a redundant and scalable fiber optic network across multiple towns. The lack of network redundancy was a single point of failure, impacting service reliability and hindering expansion.",
    solution:
      "We were engaged to manage the end-to-end rollout. This involved conducting initial town surveys, designing efficient transmission ring architectures, and managing the technical and construction teams on the ground. We developed a proprietary feasibility calculator to optimize planning and ensure projects remained within budgetary constraints.",
    results: [
     " Redundant Network Designed and Built for 9 towns, significantly improving service reliability.",
"Successfully Migrated 595+ Customers with a 93% retention rate on a separate migration project.",
"Laid the foundation for the client's continued expansion into new markets.",
    ],
    keyServices: [
     "Technical Project Management",
      "FTTH Rollout",
      "Infrastructure Planning",
      "Budget & Timeline Management",
      "Vendor & Team Management",
    ],
  },
  {
    id: 3,
    title: "Enterprise Data Solution Architecture",
    category: "Architecting a Centralized Data Export Solution for Enhanced Business Intelligence",
    image: case3,
    challenge:
      " A large enterprise lacked a centralized, reliable method to export core data for reporting, analytics, and customer outreach campaigns. This led to inefficiencies, data silos, and missed opportunities.",
    solution:
      "We designed and implemented a robust solution architecture to manage and automate the flow of key data. Leading the project as Scrum Master, we facilitated all Agile ceremonies, managed the product backlog, and coordinated between teams to build a secure and scalable export pipeline.",
    results: [
      "A single source of truth for data export, eliminating inconsistencies.",
      "Automated processes saved countless hours of manual data handling.",
      "Empowered the business teams with reliable data for critical analysis and targeted campaigns.",
      "Project delivered successfully through iterative sprints and continuous stakeholder feedback",
    ],
    keyServices: [
     
      "Solution Architecture",
      "Data Management",
      "Agile Delivery (Scrum Master)",
      "Business Analysis, Process Automation"
    ],
  },
  {
    id: 4,
    title: "Service Delivery & SLA Optimization",
    category: "Driving 100% SLA Compliance and Enhancing Service for Major B2B Accounts",
    image: case4,
    challenge:
      "A telecom provider needed to ensure flawless service delivery for its key enterprise clients, where any downtime resulted in significant financial penalties and reputational damage.",
    solution:
      "We took over management of two major client accounts. We implemented rigorous service review processes, conducted deep-dive root cause analyses for any incidents, and created customized Salesforce dashboards for real-time performance monitoring. We led the development and execution of Service Improvement Plans (SIPs) to address any underlying issues proactively.",
    results: [
      "Achieved 100% SLA Compliance for managed accounts, avoiding all penalty clauses.",
      "Enhanced Customer Satisfaction through transparent communication and proactive service management.",
      "Optimized Operational Processes leading to a reduction in repeat incidents.",
      "Provided actionable insights through custom reporting, enabling better client decisions",
    ],
    keyServices: [
      "Service Level Management (SLAs)",
      "Root Cause Analysis",
      "Process Optimization",
      "Client Relationship Management",
      "Salesforce Reporting",
    ],
  },
];


const CaseStudyDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const caseStudy = caseStudies.find((c) => c.id === parseInt(id));

  if (!caseStudy) {
    return (
      <>
        <CustomNavbar />
        <Container className="py-5 text-center">
          <h3>Case Study Not Found</h3>
          <Button onClick={() => navigate(-1)}>Go Back</Button>
        </Container>
        <Footer />
      </>
    );
  }

  // Related case studies (exclude current)
  const relatedStudies = caseStudies.filter((c) => c.id !== caseStudy.id);

  return (
    <>
      <CustomNavbar />
      <section className="case-study-details py-5">
        <Container>
          <Button variant="light" className="mb-4" onClick={() => navigate(-1)}>
            ← Back to Case Studies
          </Button>

          {/* Main Case Study */}
          <Row className="align-items-center mb-5">
            <Col lg={6} className="mb-4 mb-lg-0">
              <img
                src={caseStudy.image}
                alt={caseStudy.title}
                className="img-fluid rounded shadow-sm"
              />
            </Col>
           <Col lg={6}>
  <h2 className="mb-3">{caseStudy.title}</h2>
  <p className="text-muted mb-3">{caseStudy.category}</p>

  <h5>Challenge:</h5>
  <p>{caseStudy.challenge}</p>

  <h5>The Strat Edge Solution:</h5>
  <p>{caseStudy.solution}</p>

  <h5>The Results:</h5>
  <ul>
    {caseStudy.results.map((point, index) => (
      <li key={index}>{point}</li>
    ))}
  </ul>

  <h5>Key Services:</h5>
  <ul>
    {caseStudy.keyServices.map((service, index) => (
      <li key={index}>{service}</li>
    ))}
  </ul>
</Col>

          </Row>

          {/* Related Case Studies */}
          <div className="related-studies">
            <h3 className="mb-4">Related Case Studies</h3>
            <Row>
              {relatedStudies.map((study) => (
                <Col md={6} lg={3} key={study.id} className="mb-4">
                  <div
                    className="related-card"
                    onClick={() => navigate(`/case-study/${study.id}`)}
                  >
                    <div className="related-image-wrapper">
                      <img
                        src={study.image}
                        alt={study.title}
                        className="related-image"
                      />
                    </div>
                    <h6 className="mt-2">{study.title}</h6>
                    <p className="text-muted">{study.category}</p>
                  </div>
                </Col>
              ))}
            </Row>
          </div>
        </Container>
      </section>
      <Footer />
    </>
  );
};

export default CaseStudyDetails;
